import { useRef, useEffect, useState, lazy, memo, Suspense } from "react"
import { useTranslation } from "react-i18next"

import type { CanvasItem } from "@/types/pdfBuilder"
import { colors } from "@/components/colors"
import type { ChartComponentProps } from "./types"

// 크기 변경 후 다시 그리기까지 기다리는 시간
const RESIZE_REDRAW_DEBOUNCE_MS = 80

// Lazy load chart components
const BuilderLineChart = lazy(() => import("./BuilderLineChart"))
const BuilderBarChart = lazy(() => import("./BuilderBarChart"))
const BuilderComboChart = lazy(() => import("./BuilderComboChart"))
const BuilderActivityChart = lazy(() => import("./BuilderActivityChart"))
const BuilderPointChart = lazy(() => import("./BuilderPointChart"))
const BuilderSessionDetails = lazy(() => import("./BuilderSessionDetails"))
const BuilderScatterChart = lazy(() => import("./BuilderScatterChart"))
const BuilderDataTable = lazy(() => import("./BuilderDataTable"))
const BuilderActivityInfo = lazy(() => import("./BuilderActivityInfo"))
const BuilderSummary = lazy(() => import("./BuilderSummary"))
const BuilderMap = lazy(() => import("./BuilderMap"))
const BuilderComparison = lazy(() => import("./BuilderComparison"))
const BuilderSpacer = lazy(() => import("./BuilderSpacer"))

interface ChartRendererProps {
  item: CanvasItem
}

function ChartLoader() {
  const { t } = useTranslation()

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        color: colors.neutral.grey["600"],
      }}>
      {t("loading", "Loading...")}
    </div>
  )
}

function ChartRenderer({ item }: ChartRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    // 레이아웃 px 로 잰다 — getBoundingClientRect 는 캔버스 줌(CSS transform: scale)이 곱해진 화면 px 라
    // 확대 상태에서 추가한 아이템이 칸보다 크게 그려져 오른쪽이 잘렸다 (2026-09-30 보고)
    const updateDimensions = () => {
      const el = containerRef.current
      if (el) {
        const next = { width: el.offsetWidth, height: el.offsetHeight }
        setDimensions((prev) =>
          prev.width === next.width && prev.height === next.height ? prev : next,
        )
      }
    }

    updateDimensions()

    // 크기가 바뀌는 동안(Shift+방향키 연타, 마우스 리사이즈)은 마지막 크기에서 한 번만 다시 그린다 —
    // 키마다 D3·DataGrid 를 동기로 전부 다시 그리면 키 반응이 밀렸다 (2026-09-30 보고)
    let timer: ReturnType<typeof setTimeout> | null = null
    const resizeObserver = new ResizeObserver(() => {
      if (timer !== null) {
        clearTimeout(timer)
      }
      timer = setTimeout(() => {
        timer = null
        updateDimensions()
      }, RESIZE_REDRAW_DEBOUNCE_MS)
    })
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
      if (timer !== null) {
        clearTimeout(timer)
      }
    }
  }, [])

  const chartProps: ChartComponentProps = {
    item,
    width: dimensions.width,
    height: dimensions.height,
  }

  const renderChart = () => {
    if (dimensions.width === 0 || dimensions.height === 0) {
      return null
    }

    switch (item.chartType) {
      case "activityInfo":
        return <BuilderActivityInfo {...chartProps} />
      case "summary":
        return <BuilderSummary {...chartProps} />
      case "line":
        return <BuilderLineChart {...chartProps} />
      case "bar":
        return <BuilderBarChart {...chartProps} />
      case "combo":
        return <BuilderComboChart {...chartProps} />
      case "activityGraph":
        return <BuilderActivityChart {...chartProps} />
      case "point":
        return <BuilderPointChart {...chartProps} />
      case "sessionDetails":
        return <BuilderSessionDetails {...chartProps} />
      case "scatter":
        return <BuilderScatterChart {...chartProps} />
      case "table":
        return <BuilderDataTable {...chartProps} />
      case "map":
        return <BuilderMap {...chartProps} />
      case "comparison":
        return <BuilderComparison {...chartProps} />
      case "spacer":
        return <BuilderSpacer {...chartProps} />
      default:
        return (
          <div style={{ padding: 20, color: colors.neutral.grey["800"] }}>
            Unknown chart type: {item.chartType}
          </div>
        )
    }
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <Suspense fallback={<ChartLoader />}>{renderChart()}</Suspense>
    </div>
  )
}

/**
 * 아이템 객체는 바뀐 것만 새로 만들어지므로(스토어 pages.map 이 나머지는 그대로 반환)
 * 기본 얕은 비교로 충분하다. 드래그 중 다른 차트가 따라 그려지던 것을 막는다 (#197).
 */
export default memo(ChartRenderer)
